import type { RefObject } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { cn } from '@utils/cn';
import { type FormStoryblok } from '@utils/storyblok/storyblok';
import ChevronRightSvg from '@vectors/chevron-right.svg';
import { Button } from '@common/interaction/Button';
import { ClientStoryblokComponent } from '@common/storyblok/ClientStoryblokComponent';

import { Form } from '../Form';
import { getFieldError } from './utils/getFieldError';
import { useScrollToFormError } from './utils/useScrollToFormError';

type DynamicFormProps = {
  addColumns: FormStoryblok['addColumns'];
  children?: React.ReactNode;
  defaultValues?: Record<string, unknown>;
  fields: FormStoryblok['fields'];
  formName: string;
  formRef?: RefObject<HTMLFormElement | null>;
  isPending: boolean;
  methods: UseFormReturn;
  onSubmit: () => Promise<void>;
  submitButtonText?: string;
};

export function DynamicForm({
  addColumns,
  children,
  defaultValues,
  fields,
  formName,
  formRef,
  isPending,
  methods,
  onSubmit,
  submitButtonText,
}: DynamicFormProps) {
  const {
    formState: { errors },
  } = methods;

  useScrollToFormError(methods.control, formName);

  return (
    <Form {...methods}>
      <form
        className={cn('w-full peer-first:mt-8')}
        id={formName}
        noValidate
        onSubmit={onSubmit}
        ref={formRef}
      >
        <div className="flex flex-col space-y-4">
          {addColumns ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {fields?.map((field) => {
                const isMessageField =
                  field.name.toLowerCase() === 'message' ||
                  field.name.toLowerCase() === 'bericht' ||
                  field.name.toLowerCase() === 'omschrijving';

                const isSelectField =
                  field.name.toLowerCase() === 'selectdate' ||
                  field.name.toLowerCase() === 'informatieavond';

                const isDoubleSpan = isMessageField || isSelectField;

                return (
                  <div
                    className={cn(isDoubleSpan ? 'md:col-span-2' : '')}
                    key={field.id || field._uid}
                  >
                    <ClientStoryblokComponent
                      blok={{
                        ...field,
                        defaultValue: defaultValues?.[field.name],
                        errorMessage: getFieldError(errors, field.name)?.message,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {fields?.map((field) => {
                return (
                  <ClientStoryblokComponent
                    blok={{
                      ...field,
                      defaultValue: defaultValues?.[field.name],
                      errorMessage: getFieldError(errors, field.name)?.message,
                    }}
                    key={field.id || field._uid}
                  />
                );
              })}
            </>
          )}

          {children}

          {submitButtonText && (
            <Button
              className={cn('mb-4', addColumns ? 'self-start' : 'w-full')}
              disabled={isPending}
              id={`${formName}-submit`}
              name={`${formName}-submit`}
              type="submit"
            >
              {submitButtonText}
              <ChevronRightSvg className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
